import { AddPredictions } from '@modules/feedback/components/AddPredictions';
import { getReasonSuggestionsFromSuggestedReasons } from '@modules/feedback/components/FeedbackDetails/utils';
import { FeedbackPredictionActions } from '@modules/feedback/components/FeedbackPredictionActions';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { trackFeedbackOnFeedbackV2Events } from '@modules/feedback/utils';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import { Box, Paper, Popper, Stack, Typography } from '@mui/material';
import {
  SuggestedReason,
  useGetReasonSuggestionQuery,
} from 'generated/graphql';
import { useEffect, useRef, useState } from 'react';
import { Feedback, FeedbackContentType } from 'v2/core/entities/feedback';
import { useGetUser } from 'v2/core/services/user';

interface FeedbackDetailsPredictionsHeaderProps {
  feedback: Feedback;
  suggestedReasons?: SuggestedReason[];
  areLoadingReasonSuggestions?: boolean;
}

export const FeedbackDetailsPredictionsHeader = ({
  feedback,
  suggestedReasons: suggestedReasonsProp,
  areLoadingReasonSuggestions: areReasonSuggestionsLoadingProp,
}: FeedbackDetailsPredictionsHeaderProps) => {
  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [isAddPredictionsVisible, setIsAddPredictionsVisible] = useState(false);

  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );

  const isAddingPredictionsDisabled =
    feedback.content.type === FeedbackContentType.Audio;

  const { data, loading: areReasonSuggestionsLoading } =
    useGetReasonSuggestionQuery({
      variables: {
        request: {
          recordID: feedback.id,
          tenantID,
        },
      },
      fetchPolicy: 'cache-and-network',
      skip: !!suggestedReasonsProp || isAddingPredictionsDisabled,
    });

  const suggestedReasons =
    suggestedReasonsProp ??
    ((data?.getReasonSuggestion?.suggestedReasons ?? []) as SuggestedReason[]);

  const reasonSuggestions = getReasonSuggestionsFromSuggestedReasons(
    suggestedReasons,
    editedRecords,
  );

  const onClose = () => {
    setIsAddPredictionsVisible(false);
    trackFeedbackOnFeedbackV2Events({
      eventName: 'Add Predictions View Closed',
      feedbackID: feedback.id,
      toggleLocation: 'Feedback Detail View',
    });
  };

  // This is to fix the flickering of popover when opened for the first time
  useEffect(() => {
    setAnchorEl(anchorRef.current);
  }, []);

  const onOpenAddPredictions = () => {
    setAnchorEl(anchorRef.current);
    setIsAddPredictionsVisible(true);
    dispatch({
      type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
      payload: {
        id: feedback.id,
        editPredictions: recordInEditMode?.editPredictions ?? [],
      },
    });
    trackFeedbackOnFeedbackV2Events({
      eventName: 'Add Predictions View Opened',
      feedbackID: feedback.id,
      toggleLocation: 'Feedback Detail View',
    });
  };

  return (
    <Stack width="100%">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {!isFeedbackRedesignEnabled && (
          <Typography variant="h6">Predictions</Typography>
        )}
        <Box ref={anchorRef}>
          <FeedbackPredictionActions
            isDetailedView
            feedback={feedback}
            reasonSuggestions={reasonSuggestions}
            areSuggestionsLoading={
              !!areReasonSuggestionsLoadingProp || areReasonSuggestionsLoading
            }
            isAddPredictionsVisible={isAddPredictionsVisible}
            onOpen={onOpenAddPredictions}
            onClose={onClose}
          />
          <Popper
            open={anchorEl ? isAddPredictionsVisible : false}
            anchorEl={anchorEl}
            placement="bottom-start"
            sx={{
              zIndex: (theme) => theme.zIndex.modal,
              width: '95%',
            }}
            modifiers={[
              {
                name: 'flip',
                enabled: false,
              },
            ]}
            keepMounted
            disablePortal
          >
            <Paper
              elevation={8}
              sx={{
                mt: 1,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }}
            >
              <AddPredictions
                onClose={onClose}
                isOpen={isAddPredictionsVisible}
                feedback={feedback}
                isDetailView
                reasonSuggestions={reasonSuggestions}
                areReasonSuggestionsLoading={
                  !!areReasonSuggestionsLoadingProp ||
                  areReasonSuggestionsLoading
                }
              />
            </Paper>
          </Popper>
        </Box>
      </Stack>
    </Stack>
  );
};
